// Numeric codes stored in components and sent on the wire. Append-only.

import { DOMAINS, TARGET_CLASSES, TAX_SETTINGS, TRANSPORT_MODE_KEYS } from '@taxwar/content'
import { InvariantViolation } from './errors'

export const MatchPhase = {
  setup: 0,
  regulation: 1,
  overtime: 2,
  ended: 3,
} as const
export type MatchPhase = (typeof MatchPhase)[keyof typeof MatchPhase]

/** Why the war ended (GDD §10). `none` while the war is running. */
export const OutcomeKind = {
  none: 0,
  capitalDestroyed: 1,
  regimeChange: 2,
  citiesMajority: 3,
  suddenDeath: 4,
  approvalTiebreak: 5,
  draw: 6,
  /** A seat conceded (or, online, stayed away past its reconnect grace). */
  surrender: 7,
} as const
export type OutcomeKind = (typeof OutcomeKind)[keyof typeof OutcomeKind]

/** Movement domain codes (index into content DOMAINS). */
export const DomainCode = { land: 0, sea: 1, air: 2 } as const
export type DomainCode = (typeof DomainCode)[keyof typeof DomainCode]

/** Target class codes (index into content TARGET_CLASSES). */
export const TargetClassCode = {
  any: 0,
  ground: 1,
  structuresOnly: 2,
  airOnly: 3,
  airAndGroundUnits: 4,
  none: 5,
} as const
export type TargetClassCode = (typeof TargetClassCode)[keyof typeof TargetClassCode]

/** Tax setting codes (index into content TAX_SETTINGS). */
export const TaxCode = { relief: 0, low: 1, standard: 2, high: 3, wartime: 4 } as const
export type TaxCode = (typeof TaxCode)[keyof typeof TaxCode]

/** Automatic transport mode codes (index into content TRANSPORT_MODE_KEYS): a Port's boats, an Airbase's planes. */
export const TransportModeCode = { sea: 0, air: 1 } as const
export type TransportModeCode = (typeof TransportModeCode)[keyof typeof TransportModeCode]

/**
 * Where a transport craft is in its automatic trip (taxgw-8lvi, taxgw-rvwq, taxgw-6gcs, taxgw-lcaj). idle is an
 * unloaded craft resting at its home berth, waiting for dispatch; pickup is travelling to a reserved booked group's
 * pickup city; outbound is carrying cargo to its landing; returning is heading home after a delivery or a cancelled
 * pickup — every craft rests only at its home berth, and one serving its home may be dispatched again on the way — or
 * an orphan's trip home to retire.
 */
export const TransportPhase = { idle: 0, outbound: 1, returning: 2, pickup: 3 } as const
export type TransportPhase = (typeof TransportPhase)[keyof typeof TransportPhase]

/**
 * How a ground unit takes part in the war (taxgw-8lvi, taxgw-6gcs): `ashore`, `needsTransport`, `bound` (booked on a
 * craft, walking to its pickup) and `queued` (booked, waiting at its pickup) units all take part independently — they
 * attack, capture, contest, take damage, give sight and can be targeted. Only `embarked` is cargo: the hull carries it
 * and it participates in nothing until it lands.
 */
export const PassengerState = { ashore: 0, needsTransport: 1, queued: 2, embarked: 3, bound: 4 } as const
export type PassengerState = (typeof PassengerState)[keyof typeof PassengerState]

// The code tables above mirror content key order; verify at load so they can never drift.
function assertMirrors(name: string, codes: Readonly<Record<string, number>>, keys: readonly string[], toKey: (code: string) => string): void {
  for (const [code, value] of Object.entries(codes)) {
    if (keys[value] !== toKey(code)) throw new InvariantViolation(`net ${name} code ${code}=${value} does not match content key order`)
  }
  if (Object.keys(codes).length !== keys.length) throw new InvariantViolation(`net ${name} codes do not cover every content key`)
}

const snake = (camel: string): string => camel.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)
assertMirrors('DomainCode', DomainCode, DOMAINS, (c) => c)
assertMirrors('TargetClassCode', TargetClassCode, TARGET_CLASSES, snake)
assertMirrors('TaxCode', TaxCode, TAX_SETTINGS, (c) => c)
assertMirrors('TransportModeCode', TransportModeCode, TRANSPORT_MODE_KEYS, (c) => c)
